import { useEffect, useRef, useState } from 'react';
import { Marker } from '../components/ItemsMap/types';

const isSameMarker = (a: Marker | undefined, b: Marker): boolean => {
  if (!a) return false;
  return (
    a.position.lat === b.position.lat &&
    a.position.lng === b.position.lng &&
    a.label === b.label
  );
};

/**
 * Accumulates and stabilizes markers for the current zoom level.
 * Prevents full marker replacement and preserves object references
 * to avoid unnecessary overlay re-renders.
 */
export const useAccumulatedMapMarkers = (
  preparedMarkers: Marker[] | undefined,
  zoom: number | undefined
) => {
  const cacheRef = useRef<Map<string, Marker>>(new Map());
  const zoomRef = useRef<number | undefined>(zoom);
  const [markers, setMarkers] = useState<Marker[]>([]);

  useEffect(() => {
    if (zoomRef.current !== zoom) {
      cacheRef.current.clear();
      zoomRef.current = zoom;
    }

    if (!preparedMarkers?.length) {
      if (cacheRef.current.size > 0 && zoomRef.current !== zoom) {
        setMarkers([]);
      }
      return;
    }

    let changed = false;
    const currentCache = cacheRef.current;

    preparedMarkers.forEach(marker => {
      const existing = currentCache.get(marker.id);

      if (!isSameMarker(existing, marker)) {
        currentCache.set(marker.id, marker);
        changed = true;
      }
    });

    if (changed || !markers.length) {
      setMarkers(Array.from(currentCache.values()));
    }
  }, [preparedMarkers, zoom]);

  return markers;
};
